// ────────────────────────────────────────────────────────────────────────────
// Human-readable frequency summary for a set of availability slots.
//
// This is the SINGLE source of truth, shared by the server availability parser
// (`packages/api/src/services/availability-parser.ts`) and the agency
// case-creation wizard. It uses MERGE-PER-DAY semantics: multiple ranges on the
// same weekday (or the same calendar date) collapse into their union before
// being counted, so "Mon 9-12 and Mon 1-5" is ONE Monday, and two dated slots
// on the same date are ONE occurrence. The old client copy summed slot
// durations per row instead, which double-counted split shifts and mis-labelled
// dated rows — see BEN-BACKLOG item #1.
// ────────────────────────────────────────────────────────────────────────────
function minutesOfDay(time) {
    const [hour, minute] = time.split(":").map(Number);
    return hour * 60 + minute;
}
/** Union coverage (hours) of a day's ranges, capped at 24. Overlaps count once. */
function coverageHours(slots) {
    const intervals = [];
    for (const slot of slots) {
        const start = minutesOfDay(slot.startTime);
        let end = minutesOfDay(slot.endTime);
        if (end === start) {
            intervals.push([0, 24 * 60]);
            continue;
        }
        if (end < start) {
            end += 24 * 60;
        }
        intervals.push([start, end]);
    }
    intervals.sort((a, b) => a[0] - b[0]);
    let covered = 0;
    let current;
    for (const [start, end] of intervals) {
        if (!current) {
            current = [start, end];
            continue;
        }
        if (start <= current[1]) {
            current[1] = Math.max(current[1], end);
        }
        else {
            covered += current[1] - current[0];
            current = [start, end];
        }
    }
    if (current) {
        covered += current[1] - current[0];
    }
    return Math.min(24, covered / 60);
}
/** True when recurring slots tile the entire 168-hour week with no gap. */
function isFullWeekCoverage(slots) {
    const recurringSlots = slots.filter((slot) => slot.dayOfWeek !== undefined);
    if (recurringSlots.length === 0 || recurringSlots.length !== slots.length) {
        return false;
    }
    const weekMinutes = 7 * 24 * 60;
    const intervals = [];
    for (const slot of recurringSlots) {
        const dayOffset = slot.dayOfWeek * 24 * 60;
        const start = dayOffset + minutesOfDay(slot.startTime);
        let end = dayOffset + minutesOfDay(slot.endTime);
        if (end <= start) {
            end += 24 * 60;
        }
        if (end > weekMinutes) {
            intervals.push([start, weekMinutes], [0, end - weekMinutes]);
        }
        else {
            intervals.push([start, end]);
        }
    }
    intervals.sort((a, b) => a[0] - b[0]);
    let coveredUntil = 0;
    for (const [start, end] of intervals) {
        if (start > coveredUntil) {
            return false;
        }
        coveredUntil = Math.max(coveredUntil, end);
        if (coveredUntil >= weekMinutes) {
            return true;
        }
    }
    return coveredUntil >= weekMinutes;
}
function qHours(value) {
    if (Math.abs(value - Math.round(value)) < 0.01) {
        return String(Math.round(value));
    }
    return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}
function hoursLabel(value) {
    return `${qHours(value)} ${Math.abs(value - 1) < 0.01 ? "hour" : "hours"}`;
}
export function frequencyFromAvailabilitySlots(slots) {
    if (slots.length === 0) {
        return "";
    }
    const recurringSlotsByDay = new Map();
    const datedSlotsByDate = new Map();
    for (const slot of slots) {
        if (slot.dayOfWeek !== undefined) {
            const daySlots = recurringSlotsByDay.get(slot.dayOfWeek) ?? [];
            daySlots.push(slot);
            recurringSlotsByDay.set(slot.dayOfWeek, daySlots);
        }
        else if (slot.date) {
            const dateSlots = datedSlotsByDate.get(slot.date) ?? [];
            dateSlots.push(slot);
            datedSlotsByDate.set(slot.date, dateSlots);
        }
    }
    const recurring = new Map([...recurringSlotsByDay.entries()].map(([day, daySlots]) => [day, coverageHours(daySlots)]));
    const dated = new Map([...datedSlotsByDate.entries()].map(([date, dateSlots]) => [date, coverageHours(dateSlots)]));
    const termsFor = (values, unit) => {
        const counts = new Map();
        for (const hours of values) {
            const key = qHours(hours);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        return [...counts.entries()]
            .sort((a, b) => Number(b[0]) - Number(a[0]))
            .map(([hours, count]) => `${hours}h×${count} ${unit}${count === 1 ? "" : "s"}`)
            .join(", ");
    };
    if (recurring.size > 0 && dated.size === 0) {
        const total = [...recurring.values()].reduce((sum, hours) => sum + hours, 0);
        const dayLabel = recurring.size === 1 ? "day" : "days";
        if (isFullWeekCoverage(slots)) {
            return "168 hours/week — 24h×7 days";
        }
        return `${recurring.size} ${dayLabel}/week, ${hoursLabel(total)}/week — ${termsFor(recurring.values(), "day")}`;
    }
    if (dated.size > 0 && recurring.size === 0) {
        const total = [...dated.values()].reduce((sum, hours) => sum + hours, 0);
        const label = dated.size === 1 ? "date" : "dates";
        return `${dated.size} one-off ${label}, ${hoursLabel(total)} total — ${termsFor(dated.values(), "date")}`;
    }
    return `${recurring.size} recurring days/week + ${dated.size} one-off dates`;
}
//# sourceMappingURL=availability-frequency.js.map